import { html, nothing } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import type { ServiceNowTicketNote } from '../../db/ServiceNowTicket.js';
import * as m from '../../localization/out/messages.js';

/**
 * Format a ServiceNow note timestamp (`yyyy-mm-dd hh:mm:ss`) as `dd/mm/yyyy HH:MM`.
 *
 * Parsed by hand rather than through `new Date()`: SN's string carries no timezone, so letting the
 * browser interpret it would shift every note by the viewer's UTC offset.
 */
export function formatNoteDate(raw: string | null | undefined): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(raw ?? '');
  if (!match) return raw ?? '';
  const [, year, month, day, hour, minute] = match;
  return `${day}/${month}/${year} ${hour}:${minute}`;
}

/**
 * Work notes, straight from ServiceNow. Bodies are sanitized server-side (api/_ticket-notes.ts)
 * because SN mixes markup into the note text and this renders it with unsafeHTML. Shared by the
 * colleague card and the FE's own ticket page so the two show notes identically.
 */
export function notesBlock(notes: ServiceNowTicketNote[] | null | undefined) {
  const list = notes ?? [];
  if (list.length === 0) return nothing;
  return html`
    <div class="rounded-lg bg-info/10 border border-info/30 px-3 py-2 flex flex-col gap-2">
      <span class="text-[11px] font-semibold uppercase tracking-wide text-info">
        ${m.colleague_notes_title()}
      </span>
      <!-- divide-y draws the separator between notes only, so a single note gets no rule. -->
      <div class="flex flex-col divide-y divide-info/25">
        ${list.map(
          (note, index) => html`
            <div class="flex flex-col gap-0.5 ${index === 0 ? 'pb-2 last:pb-0' : 'py-2 last:pb-0'}">
              <span class="text-[10px] font-medium uppercase tracking-wide text-info/80">
                ${formatNoteDate(note.date)}
              </span>
              <div class="text-xs text-base-content/80 break-words [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-5 [&_ol]:pl-5">
                ${unsafeHTML(note.html ?? '')}
              </div>
            </div>
          `,
        )}
      </div>
    </div>
  `;
}
